'use client'

import { useState, useEffect } from 'react'
import { MdDownload, MdClose } from 'react-icons/md'

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function InstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] =
        useState<BeforeInstallPromptEvent | null>(null)
    const [showPrompt, setShowPrompt] = useState(false)
    const [isInstalled, setIsInstalled] = useState(false)

    useEffect(() => {
        if (
            window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator as any).standalone
        ) {
            setIsInstalled(true)
            return
        }

        const handler = (e: Event) => {
            e.preventDefault()
            setDeferredPrompt(e as BeforeInstallPromptEvent)
            setShowPrompt(true)
        }

        window.addEventListener('beforeinstallprompt', handler)

        return () => {
            window.removeEventListener('beforeinstallprompt', handler)
        }
    }, [])

    const handleInstallClick = async () => {
        if (!deferredPrompt) return

        deferredPrompt.prompt()
        const { outcome } = await deferredPrompt.userChoice

        if (outcome === 'accepted') {
            setShowPrompt(false)
            setDeferredPrompt(null)
        }
    }

    const handleDismiss = () => {
        setShowPrompt(false)
        const dismissed = localStorage.getItem('pwa-install-dismissed')
        if (!dismissed) {
            localStorage.setItem(
                'pwa-install-dismissed',
                Date.now().toString()
            )
        }
    }

    useEffect(() => {
        const dismissed = localStorage.getItem('pwa-install-dismissed')
        if (dismissed) {
            const dismissedTime = parseInt(dismissed, 10)
            const daysSinceDismissed =
                (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24)
            if (daysSinceDismissed < 7) {
                setShowPrompt(false)
            }
        }
    }, [])

    if (isInstalled || !showPrompt || !deferredPrompt) {
        return null
    }

    return (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up">
            <div className="bg-base-100 rounded-xl shadow-2xl border border-base-300/50 p-4 flex items-start gap-3">
                <div className="flex-1">
                    <h3 className="font-bold text-base-content mb-1">
                        Instalar App
                    </h3>
                    <p className="text-sm text-base-content/70 mb-3">
                        Instale o app para acesso rápido e melhor experiência
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={handleInstallClick}
                            className="btn btn-secondary btn-sm gap-2"
                        >
                            <MdDownload className="text-lg" />
                            Instalar
                        </button>
                        <button
                            onClick={handleDismiss}
                            className="btn btn-ghost btn-sm"
                        >
                            Agora não
                        </button>
                    </div>
                </div>
                <button
                    onClick={handleDismiss}
                    className="btn btn-ghost btn-sm btn-square flex-shrink-0"
                >
                    <MdClose className="text-lg" />
                </button>
            </div>
        </div>
    )
}

